import React, { useEffect, useState } from 'react';
import { strings } from '/imports/strings';
import { Page } from '/imports/ui/Page';
import { useUser } from '/imports/hooks';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { modelClass, Settings, settingsId, validateScrapeSchedules, validateManBuyTiers, BOT_VERBOSITY_OPTIONS, DEFAULT_BOT_VERBOSITY } from '/imports/api/settings';
import { autoForm, formConfig } from '/imports/ui/AutoForm';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { JsonField } from '/imports/ui/JsonField';
import { CardListPicker } from '/imports/ui/CardListPicker';
import { NumberInput } from '/imports/ui/NumberInput';
import { Select } from '/imports/ui/Select';
import { Checkbox } from '/imports/ui/Checkbox';
import { TextArea } from '/imports/ui/TextArea';

// Tailwind shell replacing the old styled(Page) wrapper. Descendant rules reach
// the textarea / .field-error / .json-field rendered by child components.
const SettingsPageStyled = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={[
      props.className ?? '',
      '[&_textarea]:h-[15rem] [&_textarea]:w-full [&_textarea]:font-mono',
      '[&_.field-error]:text-[red] [&_.field-error]:mt-1',
      '[&_.json-field]:w-full',
    ].join(' ')}
  />
);

/** A TextArea bound to an object/array Settings field. Edits a JSON string;
 *  on every change we JSON.parse + validate, writing the parsed value into the
 *  form state only when valid (so the DB value stays structured). Empty input
 *  writes `emptyValue` (e.g. {} for schedules, undefined to revert to default). */

export const SettingsPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const [initialized, setInitialized] = useState(false);
  const [settings, setSettings] = useState<Settings>();
  const [update, setUpdate] = useState<Partial<Settings>>({ _id: settingsId });
  useEffect(() => {
    (async () => {
      setSettings(await modelClass.getMethod.call(settingsId));
      setInitialized(true);
    })();
  }, []);
  const [SettingsForm] = autoForm({
    document: settings,
    createMethod: modelClass.updateMethod as any,
    updateMethod: modelClass.updateMethod as any,
    update, setUpdate,
    form: formConfig<Settings>([
      [{ field: 'playwrightDisplayServer', label: 'Playwright Display Server' }],
      [{ field: 'goatbotsScrapeSchedules', input: () => (
        <JsonField<Settings>
          field="goatbotsScrapeSchedules"
          label={'Goatbots Scrape Schedules — JSON { "<cron>": ["SET", ...] }'}
          initial={settings?.goatbotsScrapeSchedules}
          validate={validateScrapeSchedules}
          emptyValue={{}}
          setUpdate={setUpdate}
        />
      ) }],
      [{ field: 'goatbotsManBuyTiers', input: () => (
        <JsonField<Settings>
          field="goatbotsManBuyTiers"
          label={'Goatbots Man Buy Tiers — JSON [[cutoff, factor], ...] (e.g. [[1, 0.9], [0.1, 0.75], [0, 0.5]])'}
          initial={settings?.goatbotsManBuyTiers}
          validate={validateManBuyTiers}
          emptyValue={null}
          setUpdate={setUpdate}
        />
      ) }],
      [{ field: 'goatbotsManSellSpreadDivisor', label: 'Goatbots Man Sell Spread Divisor', input: { type: NumberInput } }],
      // Pick list -> stored as a number. Select emits a string, so coerce on
      // change; value falls back to the saved doc, then the default.
      [{ field: 'botVerbosity', input: (setUpdate, update) => (
        <div className="json-field">
          <label>Default Bot Verbosity</label>
          <Select
            value={String(update.botVerbosity ?? settings?.botVerbosity ?? DEFAULT_BOT_VERBOSITY)}
            options={BOT_VERBOSITY_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
            onChange={(v) => setUpdate({ ...update, botVerbosity: Number(v) })}
          />
        </div>
      ) }],
      [{
        field: 'featuredSet', label: 'Featured Set (site only)',
        tip: 'Set code (e.g. HOB). Drives the public site\'s default /prices set and its "Latest" badge. '
          + 'No longer used for bot advertising -- that is now Classified Query below.',
      }],
      [{ field: 'classifiedCardListId', input: (setUpdate, update) => (
        <div className="json-field">
          <label>Classified Card List (fallback)</label>
          <div className="mb-1 text-caption text-fg-3">What bots advertise when their fleet has no list of its own.</div>
          <CardListPicker
            value={'classifiedCardListId' in ((update ?? {}) as object) ? (update as any).classifiedCardListId : settings?.classifiedCardListId}
            onChange={(id) => setUpdate((prev: any) => ({ ...prev, classifiedCardListId: id }))}
            inheritLabel="No selection (bots advertise nothing)"
          />
        </div>
      ) }],
      [{ field: 'buyCardListId', input: (setUpdate, update) => (
        <div className="json-field">
          <label>Buy Card List (fallback)</label>
          <div className="mb-1 text-caption text-fg-3">What buy/hybrid bots BUY when their fleet has no list. With neither, legacy Buy Targets govern.</div>
          <CardListPicker
            value={'buyCardListId' in ((update ?? {}) as object) ? (update as any).buyCardListId : settings?.buyCardListId}
            onChange={(id) => setUpdate((prev: any) => ({ ...prev, buyCardListId: id }))}
            inheritLabel="No selection (legacy Buy Targets)"
          />
        </div>
      ) }],
      [{
        field: 'buyCap', label: 'Buy Cap (fallback)', input: { type: NumberInput },
        tip: 'Ownership cap per bought card for fleets without their own. Blank means the historic default of 4.',
      }],
      [{ field: 'sellCardListId', input: (setUpdate, update) => (
        <div className="json-field">
          <label>Sell Card List (fallback)</label>
          <div className="mb-1 text-caption text-fg-3">What sell/hybrid bots stock and sell when their fleet has no list.</div>
          <CardListPicker
            value={'sellCardListId' in ((update ?? {}) as object) ? (update as any).sellCardListId : settings?.sellCardListId}
            onChange={(id) => setUpdate((prev: any) => ({ ...prev, sellCardListId: id }))}
            inheritLabel="No selection (legacy Sell Targets)"
          />
        </div>
      ) }],
      [{
        field: 'replenishPriority', label: 'Replenish Priority (fallback)', input: { type: NumberInput },
        tip: 'Depot replenish priority for fleets without their own (higher = first). Blank means 2.',
      }],
      [{
        field: 'defaultBuyPrice', label: 'Default Buy Price (junk floor)', input: { type: NumberInput },
        tip: 'Floor price for cards with NO real buy price. Bots buy them LAST, after all priced cards, and ledger them as junk. Blank = do not buy unpriced cards. Overridden by fleet and bot-level values.',
      }],
      [{
        field: 'defaultSellPrice', label: 'Default Sell Price (junk floor)', input: { type: NumberInput },
        tip: 'Floor price for SELLING junk — applied only when a card has neither a real buy nor a real sell price. Junk becomes sellable stock (binder + deplete keep it) instead of draining to the depot. Blank = junk is not resold.',
      }],
      // Base of the chain, so a plain checkbox: there is nothing below this to
      // inherit from, and unchecked and unset mean the same thing here. Fleet
      // and bot get the three-way Inherit/Yes/No control instead.
      [{
        field: 'overdraft', label: 'Overdraft (buy on credit)', input: { type: Checkbox },
        tip: 'Bots keep buying once their tickets run out and settle the shortfall as store credit, instead of stopping. Applies to every bot that does not decide for itself — a fleet\'s overdraft overrides this, and a bot\'s own config.overdraft overrides both.',
      }],
      [{
        field: 'preferredB2bChains', label: 'Preferred B2B Chains',
        tip: 'Bot-to-bot trading: the bot chains our eager bots court first, as comma-separated MTGO name prefixes (case-insensitive; a leading underscore on the chain\'s name is ignored). They also get the trusted scoring bonus and may bank fractional credit for us. Blank = the built-in default: DojoTrade, CardBuying, Cardhoarder, Manatrader.',
      }],
      [{
        field: 'b2bPreferredOnly', label: 'B2B trade with ONLY preferred chains', input: { type: Checkbox },
        tip: 'Eager bots invite no chain outside the preferred list: every tier, encores and the manual invite included. Inbound trades from other chains are still accepted.',
      }],
      [{
        field: 'b2bGroups', label: 'B2B Bot Groups', input: { type: TextArea, props: { className: 'min-h-[16rem] w-full font-mono' } },
        tip: 'Group cycling: the bot groups the eager bots walk when selling, one per line as "Group name: bot, bot, bot" (exact MTGO usernames, case-insensitive; # starts a comment). Up to two bots are sampled per group, then the next group; nothing outside the list is invited. Takes effect on save.',
      }],
      [{
        field: 'b2bGroupCycling', label: 'B2B group cycling', input: { type: Checkbox },
        tip: 'Walk the groups above instead of the scored random pick over every advertiser. Blocked chains stay out; the 24 h cancel-on-sight and 30 min empty-binder rests do not apply while cycling.',
      }],
      [{
        field: 'replenishIntervalMinutes', label: 'Replenish Interval (minutes)', input: { type: NumberInput },
        tip: 'How often sell/hybrid bots compare inventory against their targets and restock from depots. Blank means 15.',
      }],
      [{
        field: 'sellCap', label: 'Sell Cap (fallback)', input: { type: NumberInput },
        tip: 'Stock level per sold card for fleets without their own. Blank falls to each bot\'s stocksize option.',
      }],
      [{
        field: 'md5', label: 'MTGO Client Checksum (md5)',
        tip: 'Read by every bot login. Extracted from FlsClient.dll by the DevTools "Client checksum" panel — normally set from there rather than typed. If MTGO ships a client update and this goes stale, every bot fails to log in.',
      }],
    ], [settings]),
  });
  return (
    <SettingsPageStyled className={`page${!initialized ? ' initializing' : ''}`}>
      <PageHeading
        left={<h1>{strings.Settings()}</h1>}
      />
      <PageBody>
        {SettingsForm}
      </PageBody>
    </SettingsPageStyled>
  );
};
